import { auth } from "./auth-client";

/**
 * Legacy email-link redemption, kept for the invite route.
 *
 * Password reset and address confirmation no longer come through here: both are
 * single API calls (`auth.resetPassword` / `auth.confirmEmail`) that need no
 * session, so they work from a different browser and cannot be defeated by a
 * mail scanner that prefetches the link. `AcceptInvite` still expects the old
 * recovery-session shape, and that is the only caller left.
 *
 * The `type` values below mirror the parameter names GoTrue put on an emailed
 * link; existing mail in inboxes still carries them.
 */
export type AuthEmailTokenType =
    | "signup"
    | "recovery"
    | "invite"
    | "magiclink"
    | "email_change"
    | "email";

const KNOWN_TOKEN_TYPES: AuthEmailTokenType[] = [
    "signup",
    "recovery",
    "invite",
    "magiclink",
    "email_change",
    "email",
];

const isAuthEmailTokenType = (value: string | null): value is AuthEmailTokenType =>
    !!value && (KNOWN_TOKEN_TYPES as string[]).includes(value);

/** A snapshot of the auth parameters an email link carried, taken before anything can scrub them. */
export interface AuthEmailParams {
    searchParams: URLSearchParams;
    hashParams: URLSearchParams;
}

/**
 * Snapshots the auth parameters currently in the URL.
 *
 * The Supabase client is configured with `detectSessionInUrl`, and the singleton is
 * created at module import — so supabase-js starts consuming `?code=` / `#access_token=`
 * and `history.replaceState`s them away before React mounts anything. Reading
 * `window.location` from inside an effect is therefore too late: the token is already
 * gone and the link looks empty. Snapshotting at import time wins that race.
 */
export const captureAuthEmailParams = (): AuthEmailParams => {
    if (typeof window === "undefined") {
        return { searchParams: new URLSearchParams(), hashParams: new URLSearchParams() };
    }
    return {
        searchParams: new URLSearchParams(window.location.search),
        hashParams: new URLSearchParams(window.location.hash.substring(1)),
    };
};

/**
 * Taken on first import of this module, which happens while the app's entry chunk
 * evaluates — ahead of supabase-js finishing its own URL detection.
 */
const initialParams = captureAuthEmailParams();

/** True when the snapshot holds nothing an auth email link would have put there. */
const isEmpty = ({ searchParams, hashParams }: AuthEmailParams): boolean =>
    !searchParams.get("token_hash") &&
    !searchParams.get("code") &&
    !searchParams.get("error") &&
    !hashParams.get("access_token") &&
    !hashParams.get("error");

/**
 * Waits for a session to appear, for the case where supabase-js is mid-flight writing one.
 *
 * `detectSessionInUrl` consumes the token asynchronously; its session write routinely
 * lands a tick or two after our first `getSession()` read. Without this wait that timing
 * difference is the difference between a working form and a bounced user.
 */
const waitForSession = async (timeoutMs = 2000, intervalMs = 150) => {
    const deadline = Date.now() + timeoutMs;

    for (;;) {
        const { data: { session } } = await auth.getSession();
        if (session) return session;
        if (Date.now() >= deadline) return null;
        await new Promise((resolve) => setTimeout(resolve, intervalMs));
    }
};

/**
 * Establishes a session from an auth email link and scrubs the token out of the URL.
 *
 * Cloudflare Under Attack Mode challenges the redirect chain behind Supabase's
 * `/auth/v1/verify` endpoint, and that endpoint burns its single-use token on *any*
 * GET — so a mail scanner prefetching the link kills it before the user clicks.
 * Emailing a bare `token_hash` and redeeming it here avoids both: the token rides
 * the query string through any challenge redirect, needs no PKCE verifier (so it
 * works from a different browser or device), and is only consumed by this call.
 *
 * The remaining branches redeem links that were already sitting in inboxes when
 * this shipped. Those are exactly the shapes supabase-js also claims, so each one
 * falls back to the session it may have established for us rather than failing.
 *
 * @param fallbackType Used when the link carries no recognisable `type` parameter.
 * @param params Parameter snapshot to redeem. Defaults to the one taken at import.
 * @throws When no token is present or Supabase rejects the one that is.
 */
export const consumeAuthEmailToken = async (
    fallbackType: AuthEmailTokenType,
    params?: AuthEmailParams
): Promise<void> => {
    // Prefer the import-time snapshot, but fall back to a live read when it is empty —
    // a client-side navigation onto this page happens after the snapshot was taken.
    const resolved = params
        ?? (isEmpty(initialParams) ? captureAuthEmailParams() : initialParams);
    const { searchParams, hashParams } = resolved;

    try {
        const error = hashParams.get("error") || searchParams.get("error");
        if (error) {
            throw new Error(
                hashParams.get("error_description") ||
                searchParams.get("error_description") ||
                "This link is invalid or has expired."
            );
        }

        const tokenHash = searchParams.get("token_hash");
        const code = searchParams.get("code");
        const accessToken = hashParams.get("access_token");

        if (tokenHash) {
            const linkType = searchParams.get("type");
            const { error: verifyError } = await auth.verifyOtp({
                type: isAuthEmailTokenType(linkType) ? linkType : fallbackType,
                token_hash: tokenHash,
            });
            // supabase-js never touches `token_hash`, so a failure here is a genuinely
            // spent or malformed token — but an existing session still means the user
            // arrived with a working one (a reload after a successful redemption).
            if (verifyError && !(await waitForSession(0))) throw verifyError;
        } else if (code) {
            // Legacy PKCE links. `detectSessionInUrl` may have already redeemed this code,
            // in which case the exchange reports it as invalid even though we have a session.
            const { error: exchangeError } = await auth.exchangeCodeForSession(code);
            if (exchangeError && !(await waitForSession())) throw exchangeError;
        } else if (accessToken) {
            // Legacy implicit-flow links — likewise already claimable by supabase-js.
            const { error: sessionError } = await auth.setSession({
                access_token: accessToken,
                refresh_token: hashParams.get("refresh_token") || "",
            });
            if (sessionError && !(await waitForSession())) throw sessionError;
        } else {
            // Either supabase-js scrubbed the token before we could snapshot it, or the
            // user reloaded mid-flow and still has the session the token created.
            const session = await waitForSession();
            if (!session) {
                throw new Error("No token found in this link. Please request a new one.");
            }
        }
    } finally {
        // Drop the token from the URL so it does not linger in history or leak through
        // the Referer header on the next navigation. Also on the failure path: a spent
        // token is worth no less to a snooper, and leaving it there invites a reload
        // that just fails again.
        if (typeof window !== "undefined") {
            window.history.replaceState({}, document.title, window.location.pathname);
        }
    }
};

/**
 * Maps an auth email error to copy a user can act on. An expired or already-used
 * token is by far the most common failure and needs a different instruction than
 * a malformed link.
 */
export const describeAuthEmailError = (
    error: unknown,
    fallbackMessage = "This link is invalid. Please request a new one."
): string => {
    const code = (error as { code?: string })?.code;
    const message = error instanceof Error ? error.message : "";

    if (code === "otp_expired" || /expired/i.test(message)) {
        return "This link has expired. Please request a new one.";
    }
    return message || fallbackMessage;
};
